/* eslint-disable max-lines-per-function */
/* eslint-disable array-element-newline */
/* eslint-disable new-cap */
import {MobxReactionUpdate} from '@adobe/lit-mobx';
import {LitElement, html, nothing} from 'lit';
import {styleMap} from 'lit/directives/style-map.js';
import {classMap} from 'lit/directives/class-map.js';
import styles from './diamond-overlay.component.scss?as=stylesheet';
import {ringBuilderStore} from '../../data/ring-builder-store';
import {DIAMOND_COMPOSITES, DIAMOND_PREVIEW_MEASUREMENT_DATA} from '../../data/constants';
import {polynomial} from 'regression';
import {reaction, observable, makeObservable, action} from 'mobx';
import {defineCustomElement} from 'extensions/utils';

// TODO: Refactor static data into constants.js

class DiamondOverlay extends MobxReactionUpdate(LitElement) {

	static get styles () {
		return [styles];
	}

	static get properties () {
		return {
			halo: {
				type: Boolean
			},
			metal: {
				type: String
			},
			productCode: {
				attribute: 'product-code',
				type: String
			},
			store: {
				state: true,
				type: Object
			},
			vendor: {
				type: String
			}
		};
	}

	constructor () {
		super();

		this.halo = false;
		this.productCode = '';
		this.metal = 'white gold';
		this.vendor = '';

		this.sixProngProducts = [
			'2SMP00043',
			'2SMP00354',
			'2SMP00355',
			'2SMP00356',
			'2FSSW4650',
			'2FSSY4632',
			'2SM000536',
			'2SM000537'
		];

		this.platinumHeadProducts = [
			'2SM000545',
			'2SM000544',
			'2SMP00356',
			'2SMP00043'
		];

		this.squareShapedHaloProducts = [
			'2FSSW4753',
			'2SM000538',
			'2SM000539',
			'2SM000540'
		];

		this.precisionSetHalos = [
			'2FSSY4627',
			'2SM000523',
			'2SM000525',
			'2SM000526',
			'2SM000538',
			'2SM000539'
		];

		this.measurement = 0;
		this.shape = '';

		makeObservable(this, {
			measurement: observable,
			setMeasurement: action,
			setShape: action,
			shape: observable
		});
	}

	setMeasurement (value) {
		this.measurement = value;
	}

	setShape (value) {
		this.shape = value;
	}

	async firstUpdated () {
		this.store = await ringBuilderStore;

		this.stopPreviewReaction = reaction(
			() => this.store?.diamond?.preview,
			this.setShapeMeasurement.bind(this)
		);

		this.setShapeMeasurement();
	}

	setShapeMeasurement () {
		if (this.store?.diamond?.preview?.enabled) {
			this.setShape(this.store?.diamond?.preview?.shape);
			this.setMeasurement(this.calculateMeasurementFromCarat(this.store?.diamond?.preview?.carat, this.shape));
		}
		else {
			this.setShape(this.store?.diamond?.product?.attributes?.shape);
			this.setMeasurement(this.store?.diamond?.product?.attributes?.measurement);
		}
	}

	calculateMeasurementFromCarat (carat, shape) {
		const data = DIAMOND_PREVIEW_MEASUREMENT_DATA[shape] || [];
		const {predict} = polynomial(data);

		const [, measurement] = predict(carat);

		return String(measurement);
	}

	render () {
		if (!this.shape || !this.measurement) {
			return nothing;
		}

		const overlayStyles = {
			backgroundImage: `url('${this.diamondComposite()}')`,
			transform: `translate3d(0,0,0) scale(${this.calculateDiamondScale(this.measurement)})`
		};

		const classes = {
			'diamond-overlay': true,
			'diamond-overlay--halo': this.halo,
			[`diamond-shape--${this.shape.toLowerCase()}`]: true
		};

		return html`
			<span style="${styleMap(overlayStyles)}" class="${classMap(classes)}"></span>
		`;
	}

	diamondComposite () {
		const shape = this.shape?.toLowerCase()?.replace(/ /g, '-');

		if (this.precisionSetHalo) {
			return DIAMOND_COMPOSITES.products[this.productCode?.toUpperCase()][shape];
		}

		let view = 'default';

		if (this.squareShapedHalo) {
			view = 'squarehalo';
		} else if (this.halo) {
			view = 'halo';
		} else if (shape === 'round' && this.sixProng) {
			view = 'sixprong';
		}

		return DIAMOND_COMPOSITES[shape?.toLowerCase()?.replace(/ /g, '-')][this.metalType][view];
	}

	calculateDiamondScale (measurement) {
		let divisor = 9.03;
		if (this.shape === 'Round' && this.sixProng) {
			divisor = 8.05;
		} else if (this.shape === 'Round') {
			divisor = 9.5;
			if (this.halo) {
				divisor = 7.25;
			}
		} else if (this.shape === 'Oval' && this.halo) {
			divisor = 6.5;
		}

		const measurementAmount = measurement.toLowerCase().split('x')[0].trim();

		// Set max carat size
		let length = Math.min(
			divisor,
			measurementAmount
		);

		if (this.halo) {
			// Halos add some extra padding, so we will increase the divisor
			length = Math.min(
				divisor * 1.30,
				measurementAmount
			);
		}

		// Determine Scale
		return (length / divisor).toFixed(2);
	};

	get metalType () {
		if (this.platinumHeadProduct) {
			return 'WG';
		}

		if (this.precisionSet && !this.precisionSetHalo) {
			return 'WG';
		}

		let metal = this.metal?.toLowerCase();
		switch (metal) {
			case 'yellow gold':
				return `YG`;
			case 'rose gold':
				return `RG`;
			case 'rose & white gold':
				return `RG`;
			case 'two tone':
				return `YG`;
			default:
				return `WG`;
		}
	}

	get sixProng () {
		return this.sixProngProducts?.includes(this.productCode);
	}

	get squareShapedHalo () {
		return this.squareShapedHaloProducts?.includes(this.productCode);
	}

	get platinumHeadProduct () {
		return this.platinumHeadProducts?.includes(this.productCode);
	}

	get precisionSetHalo () {
		return this.precisionSetHalos?.includes(this.productCode);
	}

	get precisionSet () {
		return this.vendor?.toLowerCase()?.includes('precision set');
	}
};

defineCustomElement('rb-diamond-overlay', DiamondOverlay);
